import { createContext, useContext, useState, useCallback, type ReactNode, type ComponentType } from 'react'

type DialogState = { type: string; [key: string]: unknown }

type DialogContextType = {
  open: (state: DialogState) => void
  replace: (state: DialogState) => void
  close: () => void
}

const DialogContext = createContext<DialogContextType | null>(null)

export function useDialog(): DialogContextType {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider')
  }
  return context
}

export type DialogRegistry<TState extends DialogState = DialogState> = Record<
  string,
  ComponentType<{ onClose: () => void; dialogState: TState }>
>

type DialogProviderProps<TState extends DialogState> = {
  registry: DialogRegistry<TState>
  children: ReactNode
}

export function DialogProvider<TState extends DialogState>({ registry, children }: DialogProviderProps<TState>) {
  const [currentDialog, setCurrentDialog] = useState<DialogState | null>(null)

  const open = useCallback((state: DialogState) => {
    setCurrentDialog(state)
  }, [])

  const replace = useCallback((state: DialogState) => {
    setCurrentDialog(state)
  }, [])

  const close = useCallback(() => {
    setCurrentDialog(null)
  }, [])

  const contextValue: DialogContextType = { open, replace, close }

  const DialogComponent = currentDialog ? registry[currentDialog.type] : null

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      {currentDialog && DialogComponent && <DialogComponent onClose={close} dialogState={currentDialog as TState} />}
    </DialogContext.Provider>
  )
}
